"use client";

import { useState } from "react";
import {
  CipherGrid,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import { HINT, LABEL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";

/*
 * The working panel of the keyed letter-by-letter ciphers (Beaufort, autokey, Porta):
 * their formula, the square or table with the selected letter lit up, and a table of the
 * first letters with the arithmetic. Vocabulary-free — every word arrives in props.
 */

export interface WorkingMark {
  /** The row of the square (the key letter's row). */
  row: number;
  /** The column of the square (the plain letter's column). */
  column: number;
}

export interface KeyedWorkingProps {
  /** One line of the rule, per bullet. */
  rules: readonly string[];
  square: {
    cells: readonly string[];
    columns: number;
    label: string;
    hint: string;
    rowHeaders: readonly string[];
    columnHeaders: readonly string[];
  };
  head: readonly string[];
  /** One entry per shown letter: the cells of its row in the table. */
  rows: readonly (readonly string[])[];
  /** Where each shown letter sits in the square. */
  marks: readonly WorkingMark[];
  /** "Show {letter} in the square" — gets `{letter}`. */
  selectLabel: string;
  /** A sentence under the table when letters were left out of it. */
  limitNote?: string;
}

export function KeyedWorking({
  rules,
  square,
  head,
  rows,
  marks,
  selectLabel,
  limitNote,
}: KeyedWorkingProps) {
  const [selected, setSelected] = useState(0);
  const current = selected < rows.length ? selected : 0;
  const mark = marks.at(current);
  const tone = (index: number): GridTone | undefined => {
    if (!mark) {
      return undefined;
    }
    const row = Math.floor(index / square.columns);
    const column = index % square.columns;
    if (row === mark.row && column === mark.column) {
      return "secondary";
    }
    return row === mark.row || column === mark.column ? "primary" : undefined;
  };
  return (
    <>
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm leading-relaxed">
        {rules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
      <div className="space-y-2">
        <p className={LABEL}>{square.label}</p>
        <CipherGrid
          cells={square.cells}
          columnHeaders={square.columnHeaders}
          columns={square.columns}
          dense
          label={square.label}
          rowHeaders={square.rowHeaders}
          tone={tone}
        />
        <p className={HINT}>{square.hint}</p>
      </div>
      {rows.length > 0 ? (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  {head.map((label) => (
                    <th className={TH} key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    className={index === current ? "bg-primary/5" : undefined}
                    // biome-ignore lint/suspicious/noArrayIndexKey: letters are positional
                    key={index}
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        className={`${TD} font-mono`}
                        // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                        key={cellIndex}
                      >
                        {cellIndex === 0 ? (
                          <button
                            aria-label={t(selectLabel, { letter: cell })}
                            aria-pressed={index === current}
                            className="rounded px-1 underline decoration-dotted underline-offset-4 hover:text-primary"
                            onClick={() => setSelected(index)}
                            type="button"
                          >
                            {cell}
                          </button>
                        ) : (
                          cell
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {limitNote ? <p className={HINT}>{limitNote}</p> : null}
        </div>
      ) : null}
    </>
  );
}
